/* eslint-disable @typescript-eslint/no-empty-function */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import {
  FromStore,
  Job,
  SystemChatRecord,
  UserChatRecord,
  Variation,
} from '@cartwhl/common/types';
import { getReferenceVariation } from '@cartwhl/common/utils/chatRecords';
import getCombinedJobStatus from '@cartwhl/common/utils/getCombinedJobStatus';
import getCombinedVariationStatus from '@cartwhl/common/utils/getCombinedVariationStatus';
import getFirstFrameFromVideo from '@cartwhl/common/utils/getFirstFrameFromVideo';
import Accordion from '@cartwhl/ui/atoms/Accordion';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Loading from '@cartwhl/ui/atoms/Loading';
import LoadingBlock from '@cartwhl/ui/atoms/LoadingBlock';
import Video from '@cartwhl/ui/atoms/Video';
import Text from '@cartwhl/ui/base/Text';
import CameraCancelledIcon from '@cartwhl/ui/icons/CameraCancelledIcon';
import CameraIcon from '@cartwhl/ui/icons/CameraIcon';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';
import SubPanel from '@cartwhl/ui/layout/SubPanel';
import AnimationPreview from '@cartwhl/ui/molecules/AnimationPreview';
import SubPanelButton from '@cartwhl/ui/molecules/SubPanelButton';
import UploadError from '@cartwhl/ui/molecules/UploadError';

import { JobOptionsDropdown } from 'components/JobOptionsDropdown';
import {
  animationGenerationStore,
  historyStore,
  navigationStore,
  projectStore,
  uiStore,
} from 'stores';

enum HISTORY_TYPE {
  BROWSE = 'BROWSE',
  MEDIA = 'MEDIA',
  TEXT_MOGEN = 'TEXT_MOGEN',
}

export default function HistoryAccordion({
  jobs,
  onClick = () => {},
  defaultValue,
  currentJobId,
  currentVariationId,
  finishedInBackground,
}: {
  jobs: FromStore<Job[]>;
  onClick?: (jobID: string, variationID: string) => void;
  defaultValue?: string[];
  currentJobId?: string;
  currentVariationId?: string;
  finishedInBackground?: Readonly<string[]>;
}) {
  return (
    <Accordion defaultValue={defaultValue}>
      {jobs.map((job) => {
        const status = getCombinedJobStatus(job);
        const isJobPending = status !== 'COMPLETED' && status !== 'FAILED';
        return (
          <Accordion.Item
            key={job.jobID}
            triggerText={job.sessionName}
            value={job.jobID}
            isCurrentJob={job.jobID === currentJobId}
            additionalElement={
              isJobPending ? (
                <Loading
                  className="text-green"
                  trackClassName="stroke-gray-15"
                />
              ) : finishedInBackground?.includes(job.jobID) ? (
                <div className="bg-green dark:border-gray-90 h-5 w-5 rounded-full border-[3px] border-white" />
              ) : null
            }
          >
            <HistoryAccordionContent
              jobID={job.jobID}
              currentJobId={currentJobId}
              currentVariationId={currentVariationId}
              onClick={onClick}
            />
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

function HistoryAccordionContent({
  jobID,
  currentJobId,
  currentVariationId,
  onClick,
}: {
  jobID: string;
  currentJobId?: string;
  currentVariationId?: string;
  onClick: (jobID: string, variationID: string) => void;
}) {
  const { fullJobs } = useSnapshot(historyStore.state);
  const [isLoading, setLoading] = useState(false);

  const job = fullJobs?.find((j) => j.jobID === jobID);

  useEffect(() => {
    async function loadJob() {
      setLoading(true);
      await historyStore.loadFullJob(jobID);
      setLoading(false);
    }
    loadJob();
  }, [jobID]);

  return (
    <>
      {isLoading ? (
        <div className="border-gray-30 dark:border-gray-60 ml-2 rounded-2xl border p-4">
          <div className="mb-2 flex justify-between">
            <LoadingBlock className="h-[18px] w-[62px]" />
            <LoadingBlock className="h-[18px] w-[32px]" />
          </div>
          <LoadingBlock className={`h-[120px] w-[162px]`} />
        </div>
      ) : (
        <div key={jobID} className="flex flex-col gap-4">
          {job?.chatHistory.map(([userRecord, systemRecord]) => {
            const prompt = userRecord.prompt;
            const animationNumbers = systemRecord?.animationNumbers?.flat();
            if (systemRecord.path === 'mu1' && animationNumbers) {
              const variations = Object.values(job.variationInfoByID).filter(
                (v) => animationNumbers.includes(Number(v.variationID)),
              );
              if (getCombinedVariationStatus(variations) === 'FAILED') {
                const lastAnimationNumber =
                  animationNumbers[animationNumbers.length - 1];
                return (
                  <div
                    className="px-2 lg:pr-0 lg:pl-2"
                    key={`${job.jobID}-${animationNumbers[0]}`}
                  >
                    <UploadError
                      errorReasons={[
                        'Ensure that a single person is in each frame',
                        'The majority of the person should be visible in each frame',
                      ]}
                      isLatestError={
                        lastAnimationNumber + 1 === job.animationNumbers.length
                      }
                      footer={
                        <Text
                          className="text-gray-60 dark:text-gray-45 pt-4"
                          as="div"
                          variant="studio/body/medium/sm"
                        >
                          Check out our{' '}
                          <Link
                            className="text-blue hover:underline"
                            href="https://docs.getcartwheel.com/docs/creating-motion/video-to-motion"
                            target="_blank"
                          >
                            documentation
                          </Link>{' '}
                          for more tips.
                        </Text>
                      }
                      primaryIcon={<CameraCancelledIcon className="text-red" />}
                    />
                  </div>
                );
              }
            }
            const recordType =
              systemRecord.path === 'mu1'
                ? HISTORY_TYPE.MEDIA
                : systemRecord.path === 'br1'
                  ? HISTORY_TYPE.BROWSE
                  : HISTORY_TYPE.TEXT_MOGEN;
            return (
              <Record
                key={systemRecord.animationNumbers?.flat().toString()}
                job={job}
                recordPair={[userRecord, systemRecord]}
                onClick={onClick}
                prompt={prompt}
                isCurrent={
                  job.jobID === currentJobId &&
                  systemRecord.animationNumbers
                    ?.flat()
                    .indexOf(Number(currentVariationId)) !== -1
                }
                type={recordType}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

function Record({
  recordPair,
  job,
  onClick,
  prompt,
  isCurrent,
  type,
}: {
  recordPair: FromStore<[UserChatRecord, SystemChatRecord]>;
  job: FromStore<Job>;
  onClick: (jobID: string, variationID: string) => void;
  prompt?: string;
  isCurrent?: boolean;
  type: HISTORY_TYPE;
}) {
  const [currentUserRecord, currentSystemRecord] = recordPair;
  const { subPanelKey } = useSnapshot(uiStore.state);
  const [image, setImage] = useState('');
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [firstFrameOfReferenceVideo, setFirstFrameOfReferenceVideo] =
    useState<string>();

  const variations = Object.values(job.variationInfoByID).filter((v) =>
    currentSystemRecord.animationNumbers
      ?.flat()
      .includes(Number(v.variationID)),
  );

  const elementToTrack = useRef<HTMLDivElement>(null);

  const selectedVariationId =
    currentSystemRecord.selectedAnimationNumber?.toString();
  const selectedVariation =
    job?.variationInfoByID[selectedVariationId] || variations[0];

  useEffect(() => {
    const renderImage = async () => {
      const image = await getFirstFrameFromVideo(selectedVariation.mp4Path);
      setImage(image);
    };
    if (selectedVariation.mp4Path) {
      renderImage();
    }
  }, [selectedVariation.mp4Path]);

  const uniqueId = `${job.jobID}-${variations
    .map((v) => v.variationID)
    .toString()}`;

  const { referenceVariation, referenceChatRecord, referenceSystemRecord } =
    getReferenceVariation(
      currentSystemRecord.selectedAnimationNumber.toString(),
      job,
    );

  useEffect(() => {
    if (selectedVariation && selectedVariation.comicMp4Path) {
      getFirstFrameFromVideo(selectedVariation.comicMp4Path).then((image) =>
        setFirstFrameOfReferenceVideo(image),
      );
    }
  }, [selectedVariation]);

  return (
    <div className="relative flex" ref={elementToTrack}>
      {subPanelKey === uniqueId && (
        <VariationsSubPanel
          job={job}
          uniqueId={uniqueId}
          record={currentSystemRecord}
          elementToTrack={elementToTrack.current}
          onClick={(variationId) => onClick(job.jobID, variationId)}
          selectedVariationId={selectedVariationId}
        />
      )}

      <div className="mr-2 ml-2 flex-1 lg:mr-0">
        {referenceVariation && referenceChatRecord && referenceSystemRecord && (
          <ReferenceVariation
            job={job}
            currentUserRecord={currentUserRecord}
            referenceChatRecord={referenceChatRecord}
            referenceSystemRecord={referenceSystemRecord}
            variationUniqueId={uniqueId}
            referenceVariation={referenceVariation}
            onClick={(variationId) => onClick(job.jobID, variationId)}
          />
        )}
        <SubPanelButton
          isOpen={uniqueId === subPanelKey}
          isCurrent={isCurrent}
          onClick={() => {
            uiStore.setSubPanelKey(uniqueId === subPanelKey ? '' : uniqueId);
          }}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            {/* COMIC TODO: Put first frame of user uploaded media here as needed */}
            <div className="flex items-center gap-2">
              {type === HISTORY_TYPE.MEDIA && (
                <div className="text-gray bg-gray-15 dark:bg-gray-90 flex h-7 w-10 flex-none items-center justify-center rounded-lg">
                  {firstFrameOfReferenceVideo ? (
                    <img
                      src={firstFrameOfReferenceVideo}
                      alt=""
                      className="h-full w-full rounded-[inherit] object-cover"
                    />
                  ) : (
                    <CameraIcon className="text-gray-45" />
                  )}
                </div>
              )}
              <Text
                variant="studio/body/medium/sm"
                className="line-clamp-3 text-left"
              >
                “{prompt}”
              </Text>
            </div>
            <div className="text-gray-45 flex gap-1">
              <Text variant="studio/body/medium/sm">({variations.length})</Text>
              <ChevronRightIcon className="-mt-0.5" />
            </div>
          </div>
          <div className="relative z-0">
            <>
              <Video
                src={selectedVariation?.mp4Path}
                className="relative z-50 aspect-162/120 h-[120px] rounded-lg border border-transparent"
                mode="autoplay"
                onLoadedData={() => setVideoLoaded(true)}
                // onError={() => setShowMedia(false)} // TODO handle fallback
                style={{
                  borderRightColor: image
                    ? 'rgba(34, 34, 34, 0.16)'
                    : 'rgba(34, 34, 34, 0)',
                }}
                crossOrigin="use-credentials"
              />
              {variations.slice(1).map((v, i) => (
                <img
                  key={v.variationID}
                  src={image}
                  className="absolute top-0 left-0 z-0 aspect-162/120 h-[120px] rounded-lg border border-transparent transition-[left] duration-150 ease-in-out"
                  style={{
                    left: image && videoLoaded ? `${(i + 1) * 4}px` : 0,
                    zIndex: 49 - i,
                    borderRightColor: 'rgba(97, 97, 97, 0.4)',
                    opacity: image && videoLoaded ? 1 : 0,
                  }}
                />
              ))}
            </>
            <div
              className={`absolute top-0 left-0 z-50 h-full w-full transition-opacity ${
                videoLoaded ? 'opacity-0' : 'opacity-100'
              }`}
            >
              <div className="flex aspect-video items-center justify-center">
                <Loading className="text-gray-60" />
              </div>
            </div>
          </div>
        </SubPanelButton>
      </div>
    </div>
  );
}

function VariationsSubPanel({
  job,
  uniqueId,
  record,
  prompt,
  elementToTrack,
  onClick,
  selectedVariationId = '0',
  linkToHistory,
}: {
  job: FromStore<Job>;
  uniqueId: string;
  record: FromStore<SystemChatRecord>;
  prompt?: string;
  elementToTrack: HTMLDivElement | null;
  onClick: (variationID: string) => void;
  selectedVariationId?: string;
  linkToHistory?: boolean;
}) {
  const { jobData, currentAnimationNumber } = useSnapshot(
    animationGenerationStore.state,
  );
  const navigationState = useSnapshot(navigationStore.state);
  const isLegacyProject =
    navigationStore.matchesRoute('home') || navigationState.path === '/home';

  const { subPanelKey } = useSnapshot(uiStore.state);

  const status = getCombinedJobStatus(job);
  const isJobPending = status !== 'COMPLETED' && status !== 'FAILED';

  const variations: FromStore<Variation[][]> =
    record.animationNumbers?.map((set) => {
      return set.map((n) => job.variationInfoByID[n]);
    }) || [];

  return (
    <SubPanel
      id={uniqueId}
      elementToTrack={elementToTrack}
      closePanel={() => uiStore.setSubPanelKey('')}
      subPanelKey={subPanelKey}
    >
      {prompt && (
        <div className="mb-4 flex justify-between gap-2 text-left">
          <Text variant="studio/body/medium/sm">“{prompt}”</Text>
          <div className="text-gray-45 flex gap-1">
            <Text variant="studio/body/medium/sm">({variations.length})</Text>
          </div>
        </div>
      )}
      {variations.map((group, i) => {
        const variationJobId = record.jobIDs?.flat()[0];
        return (
          <Fragment key={`${variationJobId}-variation-${i}`}>
            {variations.length > 1 && (
              <Text
                as="div"
                variant="studio/body/sm"
                className="text-gray-45 dark:text-gray-30 mb-4"
              >
                Motion set {i + 1}
              </Text>
            )}
            <div className="border-gray-30 dark:border-gray-75 mb-4 grid grid-cols-2 gap-2 border-b pb-4 last:mb-0 last:border-none last:pb-0">
              {group.map((variation) => (
                <div className="group relative" key={variation.variationID}>
                  {variationJobId && (
                    <JobOptionsDropdown
                      jobID={variationJobId}
                      animationNumber={variation.variationID}
                      favorite={variation.favorite}
                      sentiment={variation.feedback}
                      mp4Path={variation.mp4Path}
                      prompt={job?.prompt || 'Animation'}
                    />
                  )}

                  {isLegacyProject ? (
                    <Dropdown
                      trigger={
                        <div>
                          <AnimationPreview
                            mediaPath={variation.mp4Path}
                            mediaType="video"
                            aspect="animation"
                            active={
                              currentAnimationNumber ===
                                variation.variationID &&
                              jobData?.jobID === variationJobId
                            }
                            committed={
                              selectedVariationId === variation.variationID
                            }
                            loading={isJobPending}
                          />
                        </div>
                      }
                      side="left"
                    >
                      <Dropdown.Item
                        icon={<PlusIcon />}
                        onClick={() =>
                          projectStore.addExistingMotionToProject({
                            createAsNewProject: true,
                            existingJobID: job.jobID,
                            existingVariationNumber: Number(
                              variation.variationID,
                            ),
                          })
                        }
                      >
                        Create as new project
                      </Dropdown.Item>
                    </Dropdown>
                  ) : (
                    <AnimationPreview
                      mediaPath={variation.mp4Path}
                      mediaType="video"
                      aspect="animation"
                      active={
                        currentAnimationNumber === variation.variationID &&
                        jobData?.jobID === variationJobId
                      }
                      committed={selectedVariationId === variation.variationID}
                      loading={isJobPending}
                      onClick={() => onClick(variation.variationID)}
                    />
                  )}
                </div>
              ))}
            </div>
          </Fragment>
        );
      })}
      {linkToHistory && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            icon={<HistoryIcon />}
            onClick={() => {
              const uniqueId = `${record.jobIDs?.flat()[0]}-${record.animationNumbers
                ?.flat()
                .toString()}`;
              uiStore.setSubPanelKey(uniqueId);
            }}
          >
            View history
          </Button>
        </div>
      )}
    </SubPanel>
  );
}

function ReferenceVariation({
  currentUserRecord,
  job,
  variationUniqueId,
  referenceChatRecord,
  referenceSystemRecord,
  referenceVariation,
  onClick,
}: {
  currentUserRecord?: UserChatRecord;
  job: FromStore<Job>;
  variationUniqueId: string;
  referenceChatRecord: FromStore<UserChatRecord>;
  referenceSystemRecord: FromStore<SystemChatRecord>;
  referenceVariation: Variation;
  onClick: (variationID: string) => void;
}) {
  const { subPanelKey } = useSnapshot(uiStore.state);
  const [image, setImage] = useState('');
  const elementToTrack = useRef(null);

  useEffect(() => {
    const renderImage = async () => {
      const image = await getFirstFrameFromVideo(referenceVariation.mp4Path);
      setImage(image);
    };
    if (referenceVariation.mp4Path) {
      renderImage();
    }
  }, [referenceVariation.mp4Path]);

  const uniqueKey = `${variationUniqueId}-reference-${referenceVariation.variationID}`;

  const selectedVariationId = currentUserRecord?.animationNumber?.toString();

  return (
    <>
      <div
        className="flex items-center justify-start gap-1.5 pb-2 pl-6 xl:pl-8"
        ref={elementToTrack}
      >
        <svg
          fill="none"
          height="38"
          width="35"
          viewBox="0 0 35 38"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-none"
        >
          <path
            d="m34 9h-9c-13.2548 0-24 10.7452-24 24v4"
            stroke="#a7a7a7"
            strokeLinecap="round"
            strokeWidth="2"
          />
        </svg>

        <button
          className={`h-[36px] w-[50px] flex-none overflow-hidden rounded-lg ${
            subPanelKey === uniqueKey
              ? 'shadow-outline-blue-2'
              : 'hover:shadow-menu'
          }`}
          onClick={() => {
            uiStore.setSubPanelKey(uniqueKey === subPanelKey ? '' : uniqueKey);
          }}
        >
          {image ? (
            <img
              src={image}
              alt={referenceChatRecord.prompt}
              className="h-full w-full object-cover"
              draggable="false"
              crossOrigin="use-credentials"
            />
          ) : (
            <div className="bg-gray-30 h-full w-full animate-pulse"></div>
          )}
        </button>

        <Text variant="studio/body/sm" className="text-gray-45">
          “{referenceChatRecord.prompt.substring(0, 30)}
          {referenceChatRecord.prompt.length > 30 ? '...' : ''}”
        </Text>
      </div>
      {subPanelKey === uniqueKey && (
        <VariationsSubPanel
          job={job}
          uniqueId={uniqueKey}
          record={referenceSystemRecord}
          elementToTrack={elementToTrack.current}
          onClick={onClick}
          prompt={referenceVariation.prompt}
          selectedVariationId={selectedVariationId}
          linkToHistory
        />
      )}
    </>
  );
}

const HistoryIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.3809 6.62744C10.5459 6.62744 10.6877 6.68669 10.8062 6.80518C10.9246 6.91943 10.9839 7.05908 10.9839 7.22412V11.8008L10.2793 9.62988L12.4819 12.6196C12.5962 12.772 12.6449 12.9307 12.6279 13.0957C12.6152 13.2607 12.5391 13.3919 12.3994 13.4893C12.2471 13.5951 12.0884 13.6331 11.9233 13.6035C11.7625 13.5739 11.6187 13.4766 11.4917 13.3115L9.95557 11.2803C9.84131 11.1322 9.78418 10.9798 9.78418 10.8232V7.22412C9.78418 7.05908 9.84131 6.91943 9.95557 6.80518C10.0741 6.68669 10.2158 6.62744 10.3809 6.62744ZM10.6284 16.9741C9.94287 16.9741 9.27848 16.8662 8.63525 16.6504C7.99626 16.4388 7.40592 16.1383 6.86426 15.749C6.32682 15.3639 5.86344 14.9175 5.47412 14.4097C5.33024 14.2404 5.26888 14.0648 5.29004 13.8828C5.3112 13.7008 5.39372 13.557 5.5376 13.4512C5.69417 13.3411 5.8571 13.3052 6.02637 13.3433C6.19987 13.3813 6.3501 13.4744 6.47705 13.6226C6.79443 14.0415 7.16683 14.4054 7.59424 14.7144C8.02588 15.019 8.49561 15.2539 9.00342 15.4189C9.51546 15.5882 10.0571 15.675 10.6284 15.6792C11.3563 15.6834 12.0376 15.548 12.6724 15.2729C13.3114 15.0021 13.87 14.6276 14.3481 14.1494C14.8306 13.667 15.2072 13.1084 15.478 12.4736C15.7531 11.8346 15.8906 11.1491 15.8906 10.417C15.8906 9.68913 15.7531 9.00781 15.478 8.37305C15.2072 7.73405 14.8306 7.17546 14.3481 6.69727C13.87 6.21484 13.3114 5.83822 12.6724 5.56738C12.0376 5.29655 11.3563 5.16113 10.6284 5.16113C9.90055 5.16113 9.21924 5.29655 8.58447 5.56738C7.94971 5.83398 7.39111 6.20638 6.90869 6.68457C6.42627 7.16276 6.04753 7.71924 5.77246 8.354C5.50163 8.98454 5.36621 9.66374 5.36621 10.3916H4.06494C4.06917 9.49447 4.24268 8.65234 4.58545 7.86523C4.93245 7.07812 5.40641 6.38411 6.00732 5.7832C6.61247 5.18229 7.30859 4.71257 8.0957 4.37402C8.88704 4.03125 9.73128 3.85986 10.6284 3.85986C11.5256 3.85986 12.3698 4.03125 13.1611 4.37402C13.9525 4.7168 14.6486 5.19076 15.2495 5.7959C15.8547 6.39681 16.3286 7.09294 16.6714 7.88428C17.0142 8.67562 17.1855 9.51986 17.1855 10.417C17.1855 11.3141 17.0142 12.1584 16.6714 12.9497C16.3286 13.741 15.8547 14.4393 15.2495 15.0444C14.6486 15.6496 13.9525 16.1214 13.1611 16.46C12.3698 16.8027 11.5256 16.9741 10.6284 16.9741ZM3.30322 9.12207H6.43896C6.59977 9.12207 6.72038 9.16016 6.80078 9.23633C6.88542 9.30827 6.92562 9.40137 6.92139 9.51562C6.92139 9.62565 6.87695 9.74414 6.78809 9.87109L5.24561 12.0103C5.14404 12.1541 5.01921 12.2282 4.87109 12.2324C4.72721 12.2324 4.60238 12.1584 4.49658 12.0103L2.9541 9.87109C2.86523 9.74837 2.81868 9.62988 2.81445 9.51562C2.81022 9.40137 2.84831 9.30827 2.92871 9.23633C3.01335 9.16016 3.13818 9.12207 3.30322 9.12207Z"
        fill="currentColor"
      />
    </svg>
  );
};
